import React, { useState } from 'react';
import { Check, ChevronRight, RotateCcw, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useApp } from '@/contexts/AppContext';
import { useOnboarding, ONBOARDING_STEPS } from '@/contexts/OnboardingContext';
import { getForecastTerm } from '@/lib/terminology-config';
import type { OnboardingStepId, StepStatus } from '@/contexts/OnboardingContext';
import { WelcomeScreen } from './WelcomeScreen';
import { CompletionScreen } from './CompletionScreen';
import { StepGeneralSettings } from './StepGeneralSettings';
import { StepDataSources } from './StepDataSources';
import { StepRolesPermissions } from './StepRolesPermissions';
import { StepUserManagement } from './StepUserManagement';
import { StepDashboardLayout } from './StepDashboardLayout';

// ── Sidebar step indicator ────────────────────────────────────────────────────

interface StepIndicatorProps {
  stepNumber: number;
  name: string;
  description: string;
  status: StepStatus;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({
  stepNumber,
  name,
  description,
  status,
}) => {
  return (
    <div className="flex items-start gap-3 py-2">
      {/* Circle badge */}
      <div
        className={cn(
          'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold mt-0.5 transition-all',
          status === 'upcoming' && 'bg-muted text-muted-foreground',
          status === 'active' &&
            'bg-primary/15 text-primary ring-2 ring-primary',
          (status === 'completed' || status === 'skipped') &&
            'bg-primary text-primary-foreground',
        )}
      >
        {status === 'completed' || status === 'skipped' ? (
          <Check className="h-3.5 w-3.5" />
        ) : (
          stepNumber
        )}
      </div>
      {/* Label */}
      <div className="min-w-0">
        <p
          className={cn(
            'text-sm font-medium leading-tight',
            status === 'active' ? 'text-foreground' : 'text-muted-foreground',
            (status === 'completed' || status === 'skipped') && 'text-foreground',
          )}
        >
          {name}
        </p>
        <p className="text-xs text-muted-foreground leading-tight mt-0.5 truncate">
          {description}
        </p>
      </div>
    </div>
  );
};

// ── Main OnboardingAssistant shell ────────────────────────────────────────────

export const OnboardingAssistant: React.FC = () => {
  const { currentUser } = useApp();
  const {
    currentStep,
    dismissOnboarding,
    resetOnboarding,
    nextStep,
    prevStep,
    skipCurrentStep,
    getStepStatus,
  } = useOnboarding();

  const isWelcome = currentStep === 0;
  const isCompletion = currentStep === ONBOARDING_STEPS.length + 1;
  const isStep = !isWelcome && !isCompletion;

  const currentStepData = isStep ? ONBOARDING_STEPS[currentStep - 1] : null;
  const isLastStep = currentStep === ONBOARDING_STEPS.length;

  // For the last TYPE B step: if not expanded, clicking Finish marks it skipped
  const handleFinishOrNext = () => {
    if (isLastStep && currentStepData) {
      const isTypeB =
        currentStepData.id === 'dashboard-layout' ||
        currentStepData.id === 'user-management' ||
        currentStepData.id === 'roles-permissions';
      const isExpanded = expandedSteps.has(currentStepData.id);
      if (isTypeB && !isExpanded) {
        skipCurrentStep();
        return;
      }
    }
    nextStep();
  };

  // Type B steps start with an empty state; user can expand to see the real page
  const [expandedSteps, setExpandedSteps] = useState<Set<OnboardingStepId>>(new Set());

  const toggleExpanded = (id: OnboardingStepId) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const firstName = currentUser.name.split(' ')[0];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <WelcomeScreen firstName={firstName} />;
      case 1:
        return <StepGeneralSettings />;
      case 2:
        return <StepDataSources />;
      case 3:
        return (
          <StepRolesPermissions
            expanded={expandedSteps.has('roles-permissions')}
            onExpand={() => toggleExpanded('roles-permissions')}
          />
        );
      case 4:
        return (
          <StepUserManagement
            expanded={expandedSteps.has('user-management')}
            onExpand={() => toggleExpanded('user-management')}
          />
        );
      case 5:
        return (
          <StepDashboardLayout
            expanded={expandedSteps.has('dashboard-layout')}
            onExpand={() => toggleExpanded('dashboard-layout')}
          />
        );
      case 6:
        return <CompletionScreen />;
      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex bg-white"
      aria-modal="true"
      role="dialog"
      aria-label="Onboarding Assistant"
    >
      {/* ── LEFT SIDEBAR ──────────────────────────────────────────────────── */}
      <aside className="w-[280px] flex-shrink-0 bg-primary/5 border-r border-border flex flex-col">
        {/* Logo + app name */}
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
              <span className="text-primary-foreground text-sm font-bold">CF</span>
            </div>
            <span className="font-semibold text-sm text-foreground leading-tight">
              Access Cash Forecaster
            </span>
          </div>
          <p className="mt-3 text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
            Onboarding Setup
          </p>
        </div>

        <div className="mx-4 border-t border-border" />

        {/* Step indicators */}
        <div className="flex-1 px-4 py-3 space-y-1 overflow-y-auto">
          {ONBOARDING_STEPS.map((step) => (
            <StepIndicator
              key={step.id}
              stepNumber={step.number}
              name={step.name}
              description={step.sidebarDescription}
              status={isWelcome ? 'upcoming' : getStepStatus(step.id)}
            />
          ))}
        </div>

        {/* Sidebar footer — Restart Onboarding */}
        <div className="border-t border-border p-4 space-y-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-xs text-muted-foreground hover:text-foreground"
            onClick={resetOnboarding}
            title="Takes you back to step 1 so you can review and confirm your configuration — no settings will be lost"
          >
            <RotateCcw className="h-3 w-3 mr-2" />
            Restart Onboarding
          </Button>
        </div>
      </aside>

      {/* ── MAIN CONTENT AREA ─────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="h-16 flex-shrink-0 flex items-center justify-between px-6 border-b border-border bg-white">
          <div className="flex items-center gap-3 min-w-0">
            {isStep && currentStepData && (
              <>
                <span className="text-sm text-muted-foreground">
                  Step {currentStep} of {ONBOARDING_STEPS.length}
                </span>
                <span className="text-muted-foreground/40">·</span>
                <span className="text-sm font-medium text-foreground truncate">
                  {currentStepData.name}
                </span>
              </>
            )}
            {isWelcome && (
              <span className="text-sm font-medium text-foreground">
                Welcome to Access Cash Forecaster
              </span>
            )}
            {isCompletion && (
              <span className="text-sm font-medium text-foreground">
                Setup Complete
              </span>
            )}
          </div>

          {/* Skip Onboarding — hidden on completion */}
          {!isCompletion && (
            <Button
              variant="ghost"
              size="sm"
              className="text-sm text-muted-foreground hover:text-foreground flex-shrink-0"
              onClick={dismissOnboarding}
            >
              <X className="h-4 w-4 mr-1.5" />
              Skip Onboarding
            </Button>
          )}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {renderStepContent()}
        </div>

        {/* Bottom action bar — steps only, not Welcome or Completion */}
        {isStep && currentStepData && (
          <div className="flex-shrink-0 border-t border-border bg-white px-6 py-4 flex items-center justify-between gap-4">
            {/* Nudge message */}
            <NudgeMessage stepId={currentStepData.id} />

            {/* Navigation buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Back — hidden on step 1 */}
              {currentStep > 1 && (
                <Button variant="outline" size="sm" onClick={prevStep}>
                  ← Back
                </Button>
              )}

              {/* Skip for now — visible on all except last step */}
              {!isLastStep && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  onClick={skipCurrentStep}
                >
                  Skip for now
                </Button>
              )}

              {/* Next / Finish — last step goes to completion screen */}
              <Button size="sm" onClick={handleFinishOrNext}>
                {isLastStep ? (
                  <>Finish Setup <ChevronRight className="h-4 w-4 ml-1" /></>
                ) : (
                  <>Next <ChevronRight className="h-4 w-4 ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Nudge messages per step ───────────────────────────────────────────────────

const NudgeMessage: React.FC<{ stepId: OnboardingStepId }> = ({ stepId }) => {
  const forecastTerm = getForecastTerm(false); // e.g. "forecast" / "projection" / "outlook"

  const messages: Record<OnboardingStepId, string> = {
    'general-settings':
      `Set your financial year, currency and ${forecastTerm} defaults before your team starts. You can update these from Settings at any time.`,
    'data-sources':
      `Connecting your accounting, payroll and banking systems is what powers the ${forecastTerm}. The more you connect, the more accurate your cash position.`,
    'roles-permissions':
      'Cash Forecaster includes three default roles. Configure custom roles here before assigning users — or skip this and assign users to the default roles. You can customise roles at any time from Admin → Roles & Permissions.',
    'user-management':
      `Add your finance team so they can access their ${forecastTerm}s and reports. You can do this now or come back to it later from Settings.`,
    'dashboard-layout':
      'Configure which cards appear on the dashboard for each role. This helps each user see the most relevant information immediately.',
  };

  return (
    <p className="text-xs text-muted-foreground max-w-lg leading-relaxed">
      {messages[stepId]}
    </p>
  );
};
